import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../api';
import { getUser } from '../auth';

function UserBalance() {
  const navigate = useNavigate();
  const currentUser = getUser();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [confirmingId, setConfirmingId] = useState('');
  const [rejectingId, setRejectingId] = useState('');
  const [searchText, setSearchText] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  const [showAddBalanceForm, setShowAddBalanceForm] = useState(false);
  const [addBalanceLoading, setAddBalanceLoading] = useState(false);
  const [addBalanceForm, setAddBalanceForm] = useState({
    amount: '',
    notes: ''
  });

  useEffect(() => {
    fetchBalanceData();
  }, []);

  const fetchBalanceData = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await apiFetch('/my-balance');

      if (!response) {
        setError('لم يتم استلام رد من السيرفر');
        return;
      }

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || result.error || 'فشل جلب بيانات الرصيد');
      }

      setData(result);
    } catch (err) {
      setError(err.message || 'حدث خطأ أثناء جلب بيانات الرصيد');
    } finally {
      setLoading(false);
    }
  };

  const normalizeArabic = (value) => {
    return String(value || '')
      .toLowerCase()
      .replace(/[أإآا]/g, 'ا')
      .replace(/[ى]/g, 'ي')
      .replace(/[ؤ]/g, 'و')
      .replace(/[ئ]/g, 'ي')
      .replace(/[ة]/g, 'ه')
      .replace(/[٠-٩]/g, (digit) => '٠١٢٣٤٥٦٧٨٩'.indexOf(digit))
      .replace(/\s+/g, ' ')
      .trim();
  };

  const formatNumber = (value) => {
    const number = Number(value || 0);
    if (!Number.isFinite(number)) return '0';

    return number.toLocaleString('en-US', {
      maximumFractionDigits: 2
    });
  };

  const formatDateTime = (value) => {
    if (!value) return '-';

    try {
      return new Date(value).toLocaleString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      recharge: 'شحن',
      collection: 'قبض',
      transfer: 'تحويل',
      expense: 'مصروف',
      company_settlement: 'تسديد شركة',
      manual_balance_topup: 'إضافة رصيد يدوي'
    };

    return labels[type] || type || '-';
  };

  const getTypeIcon = (type) => {
    const icons = {
      recharge: '🔄',
      collection: '💵',
      transfer: '↔️',
      expense: '💸',
      company_settlement: '🏢',
      manual_balance_topup: '➕'
    };

    return icons[type] || '💳';
  };

  const handleAddBalanceChange = (e) => {
    const { name, value } = e.target;
    setAddBalanceForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const resetAddBalanceForm = () => {
    setAddBalanceForm({ amount: '', notes: '' });
  };

  const submitAddBalance = async (e) => {
    e.preventDefault();

    const amount = Number(addBalanceForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setError('أدخل مبلغ صحيح أكبر من صفر');
      return;
    }

    try {
      setAddBalanceLoading(true);
      setError('');
      setSuccessMessage('');

      const response = await apiFetch('/my-balance/add', {
        method: 'POST',
        body: JSON.stringify({
          amount,
          notes: addBalanceForm.notes || 'إضافة رصيد من صفحة رصيدي'
        })
      });

      const result = response ? await response.json().catch(() => ({})) : {};

      if (!response?.ok) {
        setError(result.message || result.error || 'فشل إضافة الرصيد');
        return;
      }

      setSuccessMessage(result.message || 'تمت إضافة الرصيد بنجاح');
      resetAddBalanceForm();
      setShowAddBalanceForm(false);
      await fetchBalanceData();
    } catch (err) {
      setError(err.message || 'حدث خطأ أثناء إضافة الرصيد');
    } finally {
      setAddBalanceLoading(false);
    }
  };

  const confirmTransfer = async (transfer) => {
    const ok = window.confirm(`هل تؤكد استلام مبلغ ${formatNumber(transfer.amount)} من ${transfer.from_user_name || 'مستخدم'}؟`);
    if (!ok) return;

    try {
      setConfirmingId(transfer.id);
      setError('');
      setSuccessMessage('');

      const response = await apiFetch(`/transfers/${transfer.id}/confirm`, {
        method: 'POST'
      });

      const result = response ? await response.json().catch(() => ({})) : {};

      if (!response?.ok) {
        setError(result.message || 'فشل تأكيد التحويل');
        return;
      }

      setSuccessMessage(result.message || 'تم تأكيد التحويل بنجاح');
      await fetchBalanceData();
    } catch (err) {
      setError('حدث خطأ أثناء تأكيد التحويل');
    } finally {
      setConfirmingId('');
    }
  };

  const rejectTransfer = async (transfer) => {
    const ok = window.confirm(`هل تريد رفض تحويل بقيمة ${formatNumber(transfer.amount)} من ${transfer.from_user_name || 'مستخدم'}؟`);
    if (!ok) return;

    try {
      setRejectingId(transfer.id);
      setError('');
      setSuccessMessage('');

      const response = await apiFetch(`/transfers/${transfer.id}/reject`, {
        method: 'POST'
      });

      const result = response ? await response.json().catch(() => ({})) : {};

      if (!response?.ok) {
        setError(result.message || 'فشل رفض التحويل');
        return;
      }

      setSuccessMessage(result.message || 'تم رفض التحويل بنجاح');
      await fetchBalanceData();
    } catch (err) {
      setError('حدث خطأ أثناء رفض التحويل');
    } finally {
      setRejectingId('');
    }
  };

  const transactions = data?.transactions || [];
  const incomingTransfers = data?.incoming_transfers || [];
  const outgoingTransfers = data?.outgoing_transfers || [];
  const allBalances = data?.all_balances || [];
  const balance = Number(data?.balance?.balance || 0);

  const filteredTransactions = useMemo(() => {
    const query = normalizeArabic(searchText);

    return transactions
      .filter((tx) => typeFilter === 'all' ? true : tx.type === typeFilter)
      .filter((tx) => {
        if (!query) return true;

        const text = normalizeArabic([
          tx.id,
          tx.reference_number,
          tx.type,
          getTypeLabel(tx.type),
          tx.amount,
          tx.description,
          tx.store_name,
          tx.wallet_name,
          tx.user_name,
          tx.related_user_name,
          tx.status,
          tx.transfer_status,
          formatDateTime(tx.created_at)
        ].filter(Boolean).join(' | '));

        return text.includes(query);
      });
  }, [transactions, searchText, typeFilter]);

  const stats = useMemo(() => {
    const result = {
      recharge: 0,
      collection: 0,
      transfer: 0,
      incoming_transfer: 0,
      outgoing_transfer: 0,
      expense: 0,
      company_settlement: 0,
      manual_balance_topup: 0,
      count: filteredTransactions.length
    };

    filteredTransactions.forEach((tx) => {
      const amount = Number(tx.amount || 0);
      if (result[tx.type] !== undefined) {
        result[tx.type] += amount;
      }

      if (tx.type === 'transfer') {
        if (String(tx.related_user_id) === String(currentUser?.id)) {
          result.incoming_transfer += amount;
        } else {
          result.outgoing_transfer += amount;
        }
      }
    });

    return result;
  }, [filteredTransactions, currentUser?.id]);

  if (loading) {
    return (
      <div dir="rtl" className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-3xl shadow p-8 text-xl font-bold text-gray-700">
          جاري تحميل رصيد المستخدم...
        </div>
      </div>
    );
  }

  return (
    <div dir="rtl" className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="bg-gradient-to-br from-gray-950 via-orange-900 to-black text-white rounded-3xl p-5 md:p-8 shadow-lg mb-6">
          <button
            onClick={() => navigate('/')}
            className="bg-white/10 hover:bg-white/20 text-white font-bold px-4 py-2 rounded-xl mb-5"
          >
            ← رجوع للرئيسية
          </button>

          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
            <div>
              <h1 className="text-3xl md:text-5xl font-bold">
                💼 رصيد المستخدم
              </h1>
              <p className="text-orange-100 mt-3 text-sm md:text-base">
                مراجعة رصيدك، إضافة رصيد يدوي، عملياتك، والتحويلات التي تنتظر تأكيدك
              </p>
            </div>

            <div className="bg-white/10 border border-white/10 rounded-3xl p-5 min-w-full lg:min-w-[320px]">
              <p className="text-orange-100 text-sm">الرصيد الحالي</p>
              <p className={`text-5xl font-bold mt-2 ${balance < 0 ? 'text-red-200' : 'text-green-200'}`}>
                {formatNumber(balance)}
              </p>
              <p className="text-orange-200 text-xs mt-2">
                {data?.balance?.name || currentUser?.name || '-'}
              </p>

              <button
                onClick={() => setShowAddBalanceForm((value) => !value)}
                className="mt-5 w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-5 rounded-xl shadow"
              >
                ➕ إضافة رصيد
              </button>
            </div>
          </div>
        </div>

        {successMessage && (
          <div className="bg-green-50 border border-green-200 text-green-700 rounded-2xl p-4 mb-4">
            ✅ {successMessage}
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl p-4 mb-4">
            ⚠️ {error}
          </div>
        )}

        {showAddBalanceForm && (
          <div className="bg-white rounded-3xl shadow p-5 md:p-6 mb-6 border border-green-100">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">➕ إضافة رصيد يدوي</h2>
                <p className="text-gray-500 text-sm mt-1">
                  استخدمها عندما تريد إضافة مبلغ إلى رصيدك لتسجيله داخل النظام.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowAddBalanceForm(false)}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold px-4 py-2 rounded-xl"
              >
                إغلاق
              </button>
            </div>

            <form onSubmit={submitAddBalance} className="grid grid-cols-1 lg:grid-cols-5 gap-3">
              <input
                type="number"
                step="0.01"
                min="0"
                name="amount"
                value={addBalanceForm.amount}
                onChange={handleAddBalanceChange}
                placeholder="المبلغ"
                className="lg:col-span-1 w-full border-2 border-gray-200 focus:border-green-500 outline-none rounded-2xl p-4 bg-gray-50"
              />

              <input
                name="notes"
                value={addBalanceForm.notes}
                onChange={handleAddBalanceChange}
                placeholder="ملاحظة اختيارية"
                className="lg:col-span-3 w-full border-2 border-gray-200 focus:border-green-500 outline-none rounded-2xl p-4 bg-gray-50"
              />

              <button
                type="submit"
                disabled={addBalanceLoading}
                className="bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white font-bold px-5 py-4 rounded-2xl"
              >
                {addBalanceLoading ? 'جاري الإضافة...' : 'تأكيد الإضافة'}
              </button>
            </form>
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-8 gap-4 mb-6">
          <SummaryCard title="عدد العمليات" value={stats.count} color="blue" />
          <SummaryCard title="قبض" value={formatNumber(stats.collection)} color="green" />
          <SummaryCard title="مصروف" value={formatNumber(stats.expense)} color="red" />
          <SummaryCard title="إضافة رصيد" value={formatNumber(stats.manual_balance_topup)} color="green" />
          <SummaryCard title="تحويلات" value={formatNumber(stats.transfer)} color="orange" />
          <SummaryCard title="وارد مؤكد" value={formatNumber(stats.incoming_transfer)} color="green" />
          <SummaryCard title="صادر مؤكد" value={formatNumber(stats.outgoing_transfer)} color="red" />
          <SummaryCard title="تحتاج تأكيد" value={incomingTransfers.length} color="yellow" />
        </div>

        {incomingTransfers.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-3xl p-5 md:p-6 mb-6">
            <h2 className="text-2xl font-bold text-yellow-900 mb-2">
              ⚠️ تحويلات واردة تنتظر تأكيدك
            </h2>
            <p className="text-sm text-yellow-800 mb-4">
              اضغط تأكيد الاستلام حتى يتم خصم المبلغ من المرسل وإضافته لرصيدك.
            </p>

            <div className="space-y-3">
              {incomingTransfers.map((transfer) => (
                <div key={transfer.id} className="bg-white rounded-2xl border border-yellow-100 p-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                  <div>
                    <p className="font-bold text-gray-900">
                      من: {transfer.from_user_name || '-'}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      المبلغ: <b>{formatNumber(transfer.amount)}</b> — {transfer.description || 'بدون وصف'}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {formatDateTime(transfer.created_at)}
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      onClick={() => confirmTransfer(transfer)}
                      disabled={confirmingId === transfer.id || rejectingId === transfer.id}
                      className="bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white font-bold px-5 py-3 rounded-xl"
                    >
                      {confirmingId === transfer.id ? 'جاري التأكيد...' : '✅ تأكيد الاستلام'}
                    </button>

                    <button
                      onClick={() => rejectTransfer(transfer)}
                      disabled={confirmingId === transfer.id || rejectingId === transfer.id}
                      className="bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white font-bold px-5 py-3 rounded-xl"
                    >
                      {rejectingId === transfer.id ? 'جاري الرفض...' : '❌ رفض التحويل'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {currentUser?.role === 'owner' && allBalances.length > 0 && (
          <div className="bg-white rounded-3xl shadow p-5 md:p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              👑 أرصدة كل المستخدمين للمالك
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {allBalances.map((item) => (
                <div key={item.user_id} className="bg-gray-50 border border-gray-100 rounded-2xl p-4">
                  <p className="font-bold text-gray-900">{item.user_name || item.name}</p>
                  <p className="text-gray-500 text-xs mt-1">{item.role === 'owner' ? 'مالك' : 'مدير'}</p>
                  <p className={`text-3xl font-bold mt-3 ${Number(item.balance || 0) < 0 ? 'text-red-600' : 'text-green-700'}`}>
                    {formatNumber(item.balance)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-white rounded-3xl shadow p-5 md:p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-5">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                🧾 عملياتي
              </h2>
              <p className="text-gray-500 text-sm mt-1">
                العمليات التي قمت بها أو تخص حسابك فقط
              </p>
            </div>

            <button
              onClick={fetchBalanceData}
              className="bg-gray-900 hover:bg-black text-white font-bold px-5 py-3 rounded-xl"
            >
              🔄 تحديث
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="بحث في عملياتي..."
              className="lg:col-span-3 w-full border-2 border-gray-200 focus:border-orange-500 outline-none rounded-2xl p-4 bg-gray-50"
            />

            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="border-2 border-gray-200 focus:border-orange-500 outline-none rounded-2xl p-4 bg-gray-50"
            >
              <option value="all">كل العمليات</option>
              <option value="recharge">شحن</option>
              <option value="collection">قبض</option>
              <option value="transfer">تحويل</option>
              <option value="expense">مصروف</option>
              <option value="company_settlement">تسديد شركة</option>
              <option value="manual_balance_topup">إضافة رصيد يدوي</option>
            </select>
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow p-5 md:p-6">
          {filteredTransactions.length === 0 ? (
            <div className="text-center text-gray-500 py-10 bg-gray-50 rounded-2xl">
              لا توجد عمليات مطابقة
            </div>
          ) : (
            <div className="space-y-3">
              {filteredTransactions.map((tx) => (
                <div key={tx.id} className="bg-gray-50 border border-gray-100 rounded-2xl p-4">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex flex-wrap gap-2 mb-2">
                        <span className="bg-orange-100 text-orange-900 rounded-full px-3 py-1 text-xs font-bold">
                          {getTypeIcon(tx.type)} {getTypeLabel(tx.type)}
                        </span>
                        <span className="bg-green-100 text-green-800 rounded-full px-3 py-1 text-xs font-bold">
                          {formatNumber(tx.amount)}
                        </span>
                        <span className="bg-gray-200 text-gray-700 rounded-full px-3 py-1 text-xs">
                          #{tx.reference_number || String(tx.id).slice(0, 8)}
                        </span>
                      </div>

                      <p className="text-gray-700 text-sm leading-7">
                        {tx.description || '-'}
                      </p>

                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2 mt-3">
                        <Info label="المحل" value={tx.store_name || '-'} />
                        <Info label="المحفظة" value={tx.wallet_name || '-'} />
                        <Info label="الطرف الآخر" value={tx.related_user_name || tx.to_user_name || '-'} />
                        <Info label="التاريخ" value={formatDateTime(tx.created_at)} />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {outgoingTransfers.length > 0 && (
            <div className="mt-6 bg-gray-50 border border-gray-100 rounded-3xl p-5">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                ↗️ تحويلاتي الصادرة
              </h2>

              <div className="space-y-2">
                {outgoingTransfers.slice(0, 10).map((transfer) => (
                  <div key={transfer.id} className="bg-white border border-gray-100 rounded-2xl p-4 text-sm">
                    إلى: <b>{transfer.to_user_name || '-'}</b> — المبلغ: <b>{formatNumber(transfer.amount)}</b> — الحالة: <b>{transfer.status}</b> — {formatDateTime(transfer.created_at)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ title, value, color }) {
  const colors = {
    blue: 'border-blue-500 text-blue-700 bg-blue-50',
    green: 'border-green-500 text-green-700 bg-green-50',
    red: 'border-red-500 text-red-700 bg-red-50',
    orange: 'border-orange-500 text-orange-700 bg-orange-50',
    yellow: 'border-yellow-500 text-yellow-700 bg-yellow-50'
  };

  return (
    <div className={`bg-white rounded-2xl shadow p-5 border-r-4 ${colors[color] || colors.blue}`}>
      <p className="text-gray-600 text-sm">{title}</p>
      <p className="text-2xl md:text-3xl font-bold mt-2">{value}</p>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="bg-white border border-gray-100 rounded-xl p-3">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className="font-bold text-gray-900 break-words">{value}</p>
    </div>
  );
}

export default UserBalance;
